import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { getActiveAccounts, saveActiveAccounts } from './lib/socialAnalyticsApi';

const emptyAccount = () => ({
  id: '',
  platform: 'Instagram',
  account: '',
  status: 'Active',
  statsSource: 'none',
  accountCount: 1,
  note: ''
});

const platformOptions = ['Instagram', 'Reddit', 'Threads', 'TikTok', 'X', 'YouTube / Shorts', 'Other'];
const statusOptions = ['Active', 'Appealing', 'Paused', 'Restricted', 'Inactive'];
const statsOptions = [
  ['none', 'No linked stats source'],
  ['reels', 'Instagram / Reels'],
  ['reddit', 'Reddit'],
  ['threads', 'Threads'],
  ['tiktok', 'TikTok'],
  ['x', 'X'],
  ['youtube', 'YouTube / Shorts']
];

const normalizeStarterAccounts = (rows) => rows.map((account, index) => ({
  ...account,
  id: account.id || `starter-${String(account.platform).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index + 1}`,
  accountCount: Number(account.accountCount) || 1
}));

const formatWholeNumber = (value) => Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '-';
const signedNumber = (value) => Number(value) > 0 ? `+${formatWholeNumber(value)}` : formatWholeNumber(value);

function ActiveAccountsPanel({ session, profile, marketingData, fallbackAccounts, onAccountsChange }) {
  const accessToken = session?.access_token;
  const canManage = ['admin', 'internal', 'model'].includes(profile?.role);
  const [accounts, setAccounts] = useState(() => normalizeStarterAccounts(fallbackAccounts));
  const [draft, setDraft] = useState(emptyAccount);
  const [editorOpen, setEditorOpen] = useState(false);
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!accessToken) return undefined;
    setWorking('load');
    getActiveAccounts({ accessToken, dashboardId: 'vasia' })
      .then((payload) => {
        if (cancelled) return;
        const loaded = Array.isArray(payload.accounts) ? payload.accounts : normalizeStarterAccounts(fallbackAccounts);
        setAccounts(loaded);
        onAccountsChange?.(loaded);
      })
      .catch((loadError) => { if (!cancelled) setError(loadError.message); })
      .finally(() => { if (!cancelled) setWorking(''); });
    return () => { cancelled = true; };
  }, [accessToken, fallbackAccounts, onAccountsChange]);

  const currentMarketing = marketingData[marketingData.length - 1];
  const activeAccountCount = accounts.reduce((sum, account) => (
    account.status === 'Active' ? sum + (Number(account.accountCount) || 1) : sum
  ), 0);
  const sortedAccounts = useMemo(() => [...accounts].sort((a, b) => (
    String(a.platform).localeCompare(String(b.platform)) || String(a.account).localeCompare(String(b.account))
  )), [accounts]);

  const persist = async (nextAccounts, successMessage) => {
    if (!accessToken) {
      setError('Sign in before changing the shared account roster.');
      return false;
    }
    setWorking('save');
    setError('');
    setMessage('');
    try {
      const payload = await saveActiveAccounts({ accessToken, dashboardId: 'vasia', accounts: nextAccounts });
      const saved = payload.accounts || nextAccounts;
      setAccounts(saved);
      onAccountsChange?.(saved);
      setMessage(successMessage);
      return true;
    } catch (saveError) {
      setError(saveError.message);
      return false;
    } finally {
      setWorking('');
    }
  };

  const startAdd = () => {
    setDraft(emptyAccount());
    setEditorOpen(true);
    setMessage('');
    setError('');
  };

  const startEdit = (account) => {
    setDraft({ ...account, accountCount: Number(account.accountCount) || 1 });
    setEditorOpen(true);
    setMessage('');
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    const normalized = {
      ...draft,
      id: draft.id || `account-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      platform: draft.platform.trim(),
      account: draft.account.trim(),
      accountCount: Math.max(1, Number.parseInt(draft.accountCount, 10) || 1),
      note: draft.note.trim()
    };
    if (!normalized.platform || !normalized.account) {
      setError('Add both a platform and account name.');
      return;
    }
    const exists = accounts.some((account) => account.id === normalized.id);
    const next = exists
      ? accounts.map((account) => account.id === normalized.id ? normalized : account)
      : [...accounts, normalized];
    if (await persist(next, exists ? `${normalized.account} was updated.` : `${normalized.account} was added.`)) {
      setEditorOpen(false);
      setDraft(emptyAccount());
    }
  };

  const remove = async (account) => {
    if (!window.confirm(`Delete ${account.account} from Active Accounts?`)) return;
    await persist(accounts.filter((item) => item.id !== account.id), `${account.account} was deleted.`);
  };

  const statusClass = (status) => {
    if (status === 'Active') return 'bg-green-500/10 text-green-300 border-green-500/30';
    if (status === 'Appealing') return 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30';
    if (status === 'Restricted') return 'bg-red-500/10 text-red-300 border-red-500/30';
    return 'bg-gray-700/70 text-gray-300 border-gray-600';
  };

  const accountStats = (account) => {
    if (!currentMarketing) return 'No marketing data available yet.';
    if (account.statsSource === 'threads' && currentMarketing.threads) {
      const data = currentMarketing.threads;
      return `${currentMarketing.period}: ${data.posts} posts, ${signedNumber(data.followersGained)} followers, ${formatWholeNumber(data.likes)} likes, ${formatWholeNumber(data.comments)} comments`;
    }
    if (account.statsSource === 'x' && currentMarketing.x) {
      const data = currentMarketing.x;
      return `${currentMarketing.period}: ${formatWholeNumber(data.impressions)} impressions, ${formatWholeNumber(data.engagements)} engagements, ${data.engagementRate}% ER`;
    }
    if (account.statsSource === 'reels' && currentMarketing.reels) {
      const data = currentMarketing.reels;
      return `${currentMarketing.period}: ${data.posted} reels, ${signedNumber(data.followersGained)} followers, ${formatWholeNumber(data.views)} views`;
    }
    return account.statsSource === 'none' ? 'No stats source selected.' : 'This source will show figures after its next successful API sync.';
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Account status</p>
          <h2 className="text-2xl font-serif text-white mb-1">Active marketing accounts</h2>
          <p className="text-gray-400 text-sm">A shared roster that Vasia and the management team can add to, edit or delete.</p>
        </div>
        {canManage && (
          <button type="button" onClick={startAdd} disabled={Boolean(working) || !accessToken} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500">
            <Plus size={16} /> Add account
          </button>
        )}
      </div>

      {message && <div className="mb-4 flex items-center gap-2 rounded-xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-100"><CheckCircle2 size={16} /> {message}</div>}
      {error && <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100"><AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}</div>}

      {editorOpen && (
        <form onSubmit={submit} className="mb-6 rounded-2xl border border-purple-500/30 bg-gray-900 p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-purple-300">{draft.id ? 'Edit account' : 'New account'}</p>
              <h3 className="mt-1 text-lg font-semibold text-white">{draft.id ? draft.account : 'Add a marketing account'}</h3>
            </div>
            <button type="button" onClick={() => setEditorOpen(false)} aria-label="Close account editor" className="rounded-lg border border-gray-700 p-2 text-gray-400 hover:text-white"><X size={16} /></button>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="text-xs text-gray-400">Platform
              <select value={platformOptions.includes(draft.platform) ? draft.platform : 'Other'} onChange={(event) => setDraft({ ...draft, platform: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white">
                {platformOptions.map((platform) => <option key={platform} value={platform}>{platform}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-400">Account name or handle
              <input required value={draft.account} onChange={(event) => setDraft({ ...draft, account: event.target.value })} placeholder="@account" className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white" />
            </label>
            <label className="text-xs text-gray-400">Status
              <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white">
                {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-400">Linked statistics
              <select value={draft.statsSource} onChange={(event) => setDraft({ ...draft, statsSource: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white">
                {statsOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-400">Number of accounts represented
              <input type="number" min="1" max="999" value={draft.accountCount} onChange={(event) => setDraft({ ...draft, accountCount: event.target.value })} className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white" />
            </label>
            <label className="text-xs text-gray-400 md:col-span-2 xl:col-span-1">Notes
              <textarea rows={3} value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} placeholder="Owner, purpose, warning or next action" className="mt-1 w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-white" />
            </label>
          </div>
          <button type="submit" disabled={Boolean(working)} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-500 disabled:bg-gray-700">
            {working === 'save' ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {draft.id ? 'Save changes' : 'Add account'}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Active Accounts</p>
          <p className="text-2xl font-bold text-green-400">{activeAccountCount}</p>
          <p className="text-xs text-gray-500 mt-1">Across {accounts.filter((account) => account.status === 'Active').length} active roster rows</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Roster Rows</p>
          <p className="text-2xl font-bold text-blue-400">{accounts.length}</p>
          <p className="text-xs text-gray-500 mt-1">Shared between authorised users</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Latest Stats Week</p>
          <p className="text-xl font-bold text-pink-400">{currentMarketing?.period || 'Not available'}</p>
          <p className="text-xs text-gray-500 mt-1">Linked per account using its stats source</p>
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        {working === 'load' && <div className="flex items-center gap-2 border-b border-gray-800 p-4 text-sm text-gray-400"><Loader2 size={15} className="animate-spin" /> Loading the shared account roster...</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-800">
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Platform</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Account</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Marketing Stats</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium">Notes</th>
              {canManage && <th className="text-right px-4 py-3 text-gray-400 font-medium">Actions</th>}
            </tr></thead>
            <tbody>
              {sortedAccounts.map((account) => (
                <tr key={account.id || `${account.platform}-${account.account}`} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 font-medium">{account.platform}</td>
                  <td className="px-4 py-3 font-semibold text-white">{account.account}{Number(account.accountCount) > 1 && <span className="ml-2 text-xs font-normal text-gray-500">x{account.accountCount}</span>}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(account.status)}`}>{account.status}</span></td>
                  <td className="px-4 py-3 text-gray-300 min-w-[300px]">{accountStats(account)}</td>
                  <td className="px-4 py-3 text-gray-400 min-w-[220px]">{account.note || '-'}</td>
                  {canManage && <td className="px-4 py-3"><div className="flex justify-end gap-2">
                    <button type="button" onClick={() => startEdit(account)} disabled={Boolean(working)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-2 text-xs text-gray-200 hover:bg-gray-800 disabled:opacity-50"><Pencil size={13} /> Edit</button>
                    <button type="button" onClick={() => remove(account)} disabled={Boolean(working)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 px-2.5 py-2 text-xs text-red-300 hover:bg-red-500/10 disabled:opacity-50"><Trash2 size={13} /> Delete</button>
                  </div></td>}
                </tr>
              ))}
              {!sortedAccounts.length && <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-10 text-center text-gray-500">No accounts yet. Use Add account to create the first roster entry.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ActiveAccountsPanel;
